import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";

import type { NativeQuote } from "../services/paymentsQuotes.service";
import type {
  EnhancedCountry,
  CountriesState,
  RuntimeWalletsState,
  ClientKycState,
  QuoteState,
  DispersionFormData,
  DispersionFormState,
} from "../types/dispersions-api.types";

export interface DispersionsState {
  countries: CountriesState;
  runtimeWallets: RuntimeWalletsState;
  clientKyc: ClientKycState;
  quote: QuoteState;
  form: DispersionFormState;
}

const initialState: DispersionsState = {
  countries: {
    countries: [],
    isLoading: false,
    error: null,
    lastFetched: null,
  },
  runtimeWallets: {
    walletsByCurrency: {},
    loadingCurrencies: [],
    errors: {},
    availableCurrencies: [],
    lastFetched: null,
    isListLoading: false,
    hasAccess: true,
  },
  clientKyc: {
    verifiedClientIds: null,
    isLoading: false,
    error: null,
  },
  quote: {
    data: null,
    isLoading: false,
    error: null,
    errorCode: null,
    lastChangedAmountField: null,
    isExpired: false,
    requestSeq: 0,
  },
  form: {
    currentStep: 1,
    formData: {
      origin_currency: "",
      destination: "",
      amount_origin: "",
      amount_destination: "",
      recipientFields: {},
      // Typing the recipient is the default path: a payment never waits on registering someone first. The
      // registered-client selects are the opt-in, and the only corridors that can offer them are the ones
      // with saved accounts — see `RecipientModeSwitch`.
      recipientMode: "manual",
      selectedClientId: null,
      selectedClientAccountId: null,
      selectedClientAccountMethod: null,
    },
    isSubmitting: false,
    submitError: null,
  },
};

export const dispersionsSlice = createSlice({
  name: "dispersions",
  initialState,
  reducers: {
    // Countries Actions
    setCountriesLoading: (state, action: PayloadAction<boolean>) => {
      state.countries.isLoading = action.payload;
    },
    setCountries: (state, action: PayloadAction<EnhancedCountry[]>) => {
      state.countries.countries = action.payload;
      state.countries.error = null;
      state.countries.lastFetched = Date.now();
    },
    setCountriesError: (state, action: PayloadAction<string>) => {
      state.countries.error = action.payload;
      state.countries.isLoading = false;
    },

    // Runtime (ledger) wallets — keyed by currency, used by the BRA/MEX corridors.
    setRuntimeWalletLoading: (state, action: PayloadAction<{ currency: string; isLoading: boolean }>) => {
      const { currency, isLoading } = action.payload;
      if (isLoading) {
        if (!state.runtimeWallets.loadingCurrencies.includes(currency)) {
          state.runtimeWallets.loadingCurrencies.push(currency);
        }
      } else {
        state.runtimeWallets.loadingCurrencies = state.runtimeWallets.loadingCurrencies.filter(
          (c) => c !== currency,
        );
      }
    },
    setRuntimeWallet: (state, action: PayloadAction<{ currency: string; wallet: RuntimeWallet | null }>) => {
      const { currency, wallet } = action.payload;
      state.runtimeWallets.walletsByCurrency[currency] = wallet;
      delete state.runtimeWallets.errors[currency];
      state.runtimeWallets.loadingCurrencies = state.runtimeWallets.loadingCurrencies.filter(
        (c) => c !== currency,
      );
    },
    setRuntimeWalletError: (state, action: PayloadAction<{ currency: string; error: string }>) => {
      const { currency, error } = action.payload;
      state.runtimeWallets.errors[currency] = error;
      state.runtimeWallets.loadingCurrencies = state.runtimeWallets.loadingCurrencies.filter(
        (c) => c !== currency,
      );
    },

    // The full wallet list, which feeds the origin selector: the user picks a
    // wallet to spend from, so the options are the wallets that exist.
    setRuntimeWalletListLoading: (state, action: PayloadAction<boolean>) => {
      state.runtimeWallets.isListLoading = action.payload;
    },
    setRuntimeWalletList: (
      state,
      action: PayloadAction<{
        wallets: RuntimeWallet[];
        /** Omitted when the catalog request failed — keep whatever was loaded. */
        availableCurrencies?: string[];
      }>,
    ) => {
      for (const wallet of action.payload.wallets) {
        state.runtimeWallets.walletsByCurrency[wallet.currency] = wallet;
      }
      if (action.payload.availableCurrencies) {
        state.runtimeWallets.availableCurrencies = action.payload.availableCurrencies;
      }
      state.runtimeWallets.lastFetched = Date.now();
      state.runtimeWallets.isListLoading = false;
    },
    setRuntimeWalletsAccessDenied: (state) => {
      state.runtimeWallets.hasAccess = false;
      state.runtimeWallets.isListLoading = false;
    },

    // Client KYC verification (shared by recipient panels and the step-2 gate). The error is kept while a
    // retry is in flight (loading does NOT clear it) so the panels' retry alert doesn't flicker; only a
    // successful fetch clears it.
    setClientKycLoading: (state, action: PayloadAction<boolean>) => {
      state.clientKyc.isLoading = action.payload;
    },
    setVerifiedClientIds: (state, action: PayloadAction<string[]>) => {
      state.clientKyc.verifiedClientIds = action.payload;
      state.clientKyc.isLoading = false;
      state.clientKyc.error = null;
    },
    setClientKycError: (state, action: PayloadAction<string>) => {
      state.clientKyc.error = action.payload;
      state.clientKyc.isLoading = false;
    },

    setQuoteLoading: (state, action: PayloadAction<boolean>) => {
      state.quote.isLoading = action.payload;
    },
    /**
     * Opens a quote request and claims the newest sequence id for it. The thunk reads the id back and drops
     * its own response if a later request has since claimed a higher one.
     */
    startQuoteRequest: (state) => {
      state.quote.requestSeq += 1;
      state.quote.isLoading = true;
    },
    setQuoteData: (state, action: PayloadAction<NativeQuote>) => {
      state.quote.data = action.payload;
      state.quote.error = null;
      state.quote.errorCode = null;
      state.quote.isLoading = false;
      // A fresh price is a fresh countdown. Without this the flag survives the re-quote and the confirm
      // button stays dead over a perfectly good quote.
      state.quote.isExpired = false;
    },
    setQuoteError: (state, action: PayloadAction<string | null>) => {
      state.quote.error = action.payload;
      if (action.payload === null) state.quote.errorCode = null;
      state.quote.isLoading = false;
    },
    setQuoteErrorCode: (state, action: PayloadAction<string | null>) => {
      state.quote.errorCode = action.payload;
    },
    setQuoteExpired: (state, action: PayloadAction<boolean>) => {
      state.quote.isExpired = action.payload;
    },
    // Field by field, not `= initialState.quote`: `requestSeq` must survive, or a response still in flight
    // would match the reset counter and install itself after the quote was deliberately cleared.
    clearQuote: (state) => {
      state.quote.data = null;
      state.quote.error = null;
      state.quote.errorCode = null;
      state.quote.isLoading = false;
      state.quote.isExpired = false;
    },
    setLastChangedAmountField: (state, action: PayloadAction<"origin" | "destination" | null>) => {
      state.quote.lastChangedAmountField = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<1 | 2 | 3>) => {
      state.form.currentStep = action.payload;
    },
    updateFormField: (state, action: PayloadAction<{ field: string; value: unknown }>) => {
      const { field, value } = action.payload;
      (state.form.formData as Record<string, unknown>)[field] = value;
    },
    updateRecipientField: (state, action: PayloadAction<{ field: string; value: unknown }>) => {
      const { field, value } = action.payload;
      state.form.formData.recipientFields[field] = value;
    },
    // A corridor change invalidates everything tied to the previous destination: the typed recipient
    // fields AND the client/account selection. Without clearing the selection, a stale account id from
    // another currency still satisfies the step-2 "account required" check while the selector renders
    // empty. The recipient mode also resets — a fresh corridor starts on the default, typing the recipient.
    clearRecipientData: (state) => {
      state.form.formData.recipientFields = {};
      state.form.formData.recipientMode = initialState.form.formData.recipientMode;
      state.form.formData.selectedClientId = null;
      state.form.formData.selectedClientAccountId = null;
      state.form.formData.selectedClientAccountMethod = null;
    },
    setFormSubmitting: (state, action: PayloadAction<boolean>) => {
      state.form.isSubmitting = action.payload;
    },
    setFormSubmitError: (state, action: PayloadAction<string | null>) => {
      state.form.submitError = action.payload;
    },
    resetForm: (state) => {
      state.form = initialState.form;
      // `requestSeq` is carried over rather than reset: rewinding it to 0 would let a request still in
      // flight from before the reset match the counter again and install its price into the fresh form.
      state.quote = { ...initialState.quote, requestSeq: state.quote.requestSeq };
    },
    /**
     * Reset the wizard and seed the next one — what the receipt's "send again" and "send to someone else"
     * actions do.
     *
     * It clears exactly what `resetForm` clears, the redeemed quote above all: a quote is single-use, so the
     * repeat is a new operation that has to be priced again. What the payload carries is only the part of the
     * finished send worth typing twice.
     *
     * Atomic on purpose. Seeding with a sequence of `updateFormField` dispatches renders the wizard once per
     * field, and step 1's quote effect would then fire against a half-seeded form.
     */
    restartFormWith: (state, action: PayloadAction<Partial<DispersionFormData>>) => {
      state.form = {
        ...initialState.form,
        formData: { ...initialState.form.formData, ...action.payload },
      };
      state.quote = { ...initialState.quote, requestSeq: state.quote.requestSeq };
    },

    resetDispersionsState: () => initialState,
  },
});

export const {
  setCountriesLoading,
  setCountries,
  setCountriesError,
  setRuntimeWalletLoading,
  setRuntimeWallet,
  setRuntimeWalletError,
  setRuntimeWalletList,
  setRuntimeWalletListLoading,
  setRuntimeWalletsAccessDenied,
  setClientKycLoading,
  setVerifiedClientIds,
  setClientKycError,
  setQuoteLoading,
  startQuoteRequest,
  setQuoteData,
  setQuoteError,
  setQuoteErrorCode,
  setQuoteExpired,
  clearQuote,
  setLastChangedAmountField,
  setCurrentStep,
  updateFormField,
  updateRecipientField,
  clearRecipientData,
  setFormSubmitting,
  setFormSubmitError,
  resetForm,
  restartFormWith,
  resetDispersionsState,
} = dispersionsSlice.actions;
